/**
 * Create Action — vertical composition with shared hooks, MediaSection, and ActionFields.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "util/UserContext";
import { useAuthGate } from "hooks/useAuthGate";
import { useMediaManager } from "hooks/useMediaManager";
import { useResolvedIssue, type IssueEntityInput } from "hooks/useResolvedIssue";
import { useCoauthorManager } from "hooks/useCoauthorManager";
import { useAutoSaveDraft } from "hooks/useAutoSaveDraft";
import { usePrivacyDefaults } from "hooks/usePrivacyDefaults";
import { usePostFormLifecycle } from "hooks/usePostFormLifecycle";
import TextEditor from "components/TextEditor";
import { createAction, addCoauthors, createCollection } from "util/posts";
import { Action } from "util/constants";
import { Loader2 } from "lucide-react";
import { saveAnonToken } from "util/anonTokenVault";
import { addAnonRef } from "util/anonIndex";
import type { LocationData } from "ui/LocationSearchField";
import TargetsModal from "components/Actions/TargetsModal";
import { ListTodo } from "lucide-react";
import { toast } from "sonner";
import DisplayOnProfilePrompt from "components/User/DisplayOnProfilePrompt";
import InputField from "ui/InputField";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { actionCreateSchema } from "lib/validation/contentSchemas";
import { PrivacyLevel } from "util/userUtils";
import PostFormShell from "components/Posts/PostFormShell";
import MediaSection from "components/Posts/sections/MediaSection";
import TitleField from "components/Posts/sections/TitleField";
import CoauthorSection from "components/Posts/sections/CoauthorSection";
import PrivacySection from "components/Posts/sections/PrivacySection";
import FormStatusMessage from "components/Posts/sections/FormStatusMessage";
import ActionFields, { type ActionPlace } from "components/Posts/kindFields/ActionFields";
import type { ActionTypeValue } from "components/Posts/kindFields/actionTypeMeta";
import { buildDraftStorageKey } from "util/draftStorage";
import { collectionApi } from "lib/api";
import { getTaskUiStrings } from "i18n/taskUi";
import { getDraftUiStrings } from "i18n/draftUi";
import ContextCommentDraftList, {
  type ContextCommentDraft,
} from "components/Posts/sections/ContextCommentDraftList";

function createTaskDraftId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `task-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Reads an optional string property from a plain object without unsafe casts.
 */
function readOptionalStringField(source: object, key: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(source, key)) {
    return undefined;
  }
  const value = Reflect.get(source, key);
  return typeof value === "string" ? value : undefined;
}

export interface ActionFormHandle {
  submit: () => void;
  saveDraft: () => Promise<void>;
  discardDraft: () => void;
}

interface Props {
  entity: IssueEntityInput;
  onSuccess: (action: Action) => void;
  onCancel: () => void;
  hideActions?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  initialDraftValue?: Record<string, unknown> | null;
}

const ActionPostForm = forwardRef<ActionFormHandle, Props>(
  (
    {
      entity,
      onSuccess,
      onCancel,
      hideActions = false,
      onCanSubmitChange,
      onSubmittingChange,
      initialDraftValue = null,
    },
    ref
  ) => {
    const { currentUser, setShowAuthModal, userIsMobile } = useUser();
    const { requireAuth } = useAuthGate();
    const navigate = useNavigate();
    const { resolvedIssue: actionEntity } = useResolvedIssue(entity);
    const { privacy, setPrivacy } = usePrivacyDefaults();
    const { coauthors, setCoauthors, addCoauthor, removeCoauthor, coauthorPairs } = useCoauthorManager(privacy);

    const mm = useMediaManager(20);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [startAt, setStartAt] = useState<Date | null>(null);
    const [endAt, setEndAt] = useState<Date | null>(null);
    const [isPrivate] = useState(false);
    const [places, setPlaces] = useState<ActionPlace[]>([]);
    const [success, setSuccess] = useState("");
    const [titleValidationMessage, setTitleValidationMessage] = useState("");
    const [showTextField, setShowTextField] = useState(false);
    const [actionType, setActionType] = useState<ActionTypeValue | "">("");
    const [targets, setTargets] = useState<{ id: number }[]>([]);
    const [targetsOpen, setTargetsOpen] = useState(false);
    const [showProfilePrompt, setShowProfilePrompt] = useState(false);
    const [newActionId, setNewActionId] = useState<number | null>(null);
    const [taskDrafts, setTaskDrafts] = useState<ContextCommentDraft[]>([]);
    const [createTaskCollection, setCreateTaskCollection] = useState(false);
    const [taskCollectionName, setTaskCollectionName] = useState<string>("");
    const taskUi = useMemo(() => getTaskUiStrings(), []);

    useEffect(() => {
      if (!initialDraftValue) {
        return;
      }
      setTitle(readOptionalStringField(initialDraftValue, "title") || "");
      setDescription(readOptionalStringField(initialDraftValue, "description") || "");
      setActionType((readOptionalStringField(initialDraftValue, "actionType") as ActionTypeValue | "") || "");
      const startAtIso = readOptionalStringField(initialDraftValue, "startAtIso");
      const endAtIso = readOptionalStringField(initialDraftValue, "endAtIso");
      setStartAt(startAtIso ? new Date(startAtIso) : null);
      setEndAt(endAtIso ? new Date(endAtIso) : null);
      const nextPlaces = initialDraftValue.places;
      setPlaces(Array.isArray(nextPlaces) ? nextPlaces as ActionPlace[] : []);
      const nextTargets = initialDraftValue.targets;
      setTargets(Array.isArray(nextTargets) ? nextTargets as { id: number }[] : []);
      const nextCoauthors = initialDraftValue.coauthors;
      setCoauthors(Array.isArray(nextCoauthors) ? nextCoauthors as typeof coauthors : []);
      setShowTextField(Boolean(initialDraftValue.showTextField));
      const nextPrivacy = readOptionalStringField(initialDraftValue, "privacy");
      setPrivacy((nextPrivacy || "public") as PrivacyLevel);
    }, [initialDraftValue, setCoauthors, setPrivacy]);

    const lifecycle = usePostFormLifecycle({
      getValues: useCallback(
        () => ({
          showTextField,
          title,
          mediaCount: mm.media.length,
          startAt,
          endAt,
          places,
          hasIssue: Boolean(actionEntity),
        }),
        [showTextField, title, mm.media.length, startAt, endAt, places, actionEntity]
      ),
      schema: actionCreateSchema,
      isReady: !mm.isUploading,
      onCanSubmitChange,
      onSubmittingChange,
    });
    const {
      validateOrError,
      setError: setLifecycleError,
      beginSubmit,
      endSubmit,
    } = lifecycle;

    const actionDraftKey = currentUser
      ? buildDraftStorageKey("draft", "action", "new", currentUser.id, actionEntity?.id ?? "none")
      : null;

    const { clearDraft } = useAutoSaveDraft({
      draftKey: actionDraftKey,
      value: {
        actionType,
        coauthors,
        description,
        endAtIso: endAt ? endAt.toISOString() : null,
        places,
        privacy,
        showTextField,
        startAtIso: startAt ? startAt.toISOString() : null,
        targets,
        title,
        // Context-comment task drafts serialize both `new` and `existing`
        // shapes so the restore path can reconstruct them byte-for-byte.
        taskDrafts: taskDrafts.map((t) =>
          t.source === "existing"
            ? {
                localId: t.localId,
                source: "existing" as const,
                existingCommentId: t.existingCommentId,
                existingPreview: t.existingPreview,
                dueAtIso: t.dueAt ? t.dueAt.toISOString() : null,
              }
            : {
                localId: t.localId,
                source: "new" as const,
                content: t.content,
                dueAtIso: t.dueAt ? t.dueAt.toISOString() : null,
              }
        ),
        createTaskCollection,
        taskCollectionName,
      },
      enabled: Boolean(currentUser),
      restoreMode: "none",
      isDirty:
        title.trim().length > 0 ||
        description.trim().length > 0 ||
        Boolean(actionType) ||
        Boolean(startAt) ||
        Boolean(endAt) ||
        places.length > 0 ||
        targets.length > 0 ||
        coauthors.length > 0 ||
        showTextField ||
        privacy !== "public" ||
        taskDrafts.some((t) => {
          if (t.source === "existing") {
            return true;
          }
          const trimmed = t.content.trim();
          return (trimmed.length > 0 && trimmed !== "<p></p>") || t.dueAt != null;
        }) ||
        createTaskCollection,
      restoreMessage: "Restore your saved action draft?",
      onRestore: (draft) => {
        setTitle(draft.title || "");
        setDescription(draft.description || "");
        setActionType((draft.actionType as ActionTypeValue | "") || "");
        setStartAt(draft.startAtIso ? new Date(draft.startAtIso) : null);
        setEndAt(draft.endAtIso ? new Date(draft.endAtIso) : null);
        setPlaces(Array.isArray(draft.places) ? draft.places : []);
        setTargets(Array.isArray(draft.targets) ? draft.targets : []);
        setCoauthors(Array.isArray(draft.coauthors) ? draft.coauthors : []);
        setShowTextField(Boolean(draft.showTextField));
        setPrivacy((draft.privacy as PrivacyLevel) || "public");
        if (Array.isArray(draft.taskDrafts)) {
          setTaskDrafts(
            draft.taskDrafts
              .map((row: {
                localId?: string;
                source?: string;
                content?: string;
                existingCommentId?: number;
                existingPreview?: string;
                dueAtIso?: string | null;
              }): ContextCommentDraft | null => {
                const localId = typeof row.localId === "string" ? row.localId : createTaskDraftId();
                const dueAt = row.dueAtIso ? new Date(row.dueAtIso) : null;
                if (row.source === "existing" && typeof row.existingCommentId === "number") {
                  return {
                    localId,
                    source: "existing",
                    existingCommentId: row.existingCommentId,
                    existingPreview:
                      typeof row.existingPreview === "string" ? row.existingPreview : "",
                    dueAt,
                  };
                }
                return {
                  localId,
                  source: "new",
                  content: typeof row.content === "string" ? row.content : "",
                  dueAt,
                };
              })
              .filter((draft): draft is ContextCommentDraft => draft !== null)
          );
        }
        setCreateTaskCollection(Boolean(draft.createTaskCollection));
        setTaskCollectionName(
          typeof draft.taskCollectionName === "string" ? draft.taskCollectionName : ""
        );
      },
      shouldSave: (draft) =>
        Boolean(
          draft.title.trim().length > 0 ||
            draft.description.trim().length > 0 ||
            draft.actionType ||
            draft.startAtIso ||
            draft.endAtIso ||
            draft.places.length > 0 ||
            draft.targets.length > 0 ||
            draft.coauthors.length > 0 ||
            draft.showTextField ||
            draft.privacy !== "public" ||
            draft.createTaskCollection ||
            (Array.isArray(draft.taskDrafts) &&
              draft.taskDrafts.some((t: {
                source?: string;
                content?: string;
                existingCommentId?: number;
                dueAtIso?: string | null;
              }) => {
                if (t.source === "existing" && typeof t.existingCommentId === "number") {
                  return true;
                }
                const trimmed = typeof t.content === "string" ? t.content.trim() : "";
                return (trimmed.length > 0 && trimmed !== "<p></p>") || Boolean(t.dueAtIso);
              }))
        ),
    });

    const uploadMediaFiles = useCallback(
      async (actionId: number) => {
        if (mm.media.length === 0) return;
        mm.setIsUploading(true);
        try {
          await uploadEntityMediaDirect(
            "action",
            actionId,
            mm.media.map((mediaFile) => ({
              file: mediaFile.file,
              order: mediaFile.order,
              intrinsic: mediaFile.intrinsic,
            }))
          );
        } finally {
          mm.setIsUploading(false);
        }
      },
      [mm]
    );

    const handlePlaceChange = useCallback((index: number, field: string, value: unknown) => {
      setPlaces((prev) => {
        const updated = [...prev];
        if (field === "is_virtual") {
          updated[index] = {
            ...updated[index],
            is_virtual: value as boolean,
            location: (value as boolean) ? { action_url: "" } : { address: "", latitude: undefined, longitude: undefined },
          };
        } else if (field === "action_url") {
          updated[index] = {
            ...updated[index],
            location: { ...updated[index].location, action_url: value as string },
          };
        } else if (field === "address") {
          updated[index] = {
            ...updated[index],
            location: { ...updated[index].location, address: value as string },
          };
        } else if (field === "location_data") {
          const loc = value as LocationData;
          updated[index] = {
            ...updated[index],
            location: {
              ...updated[index].location,
              address: loc.formatted_address,
              latitude: loc.lat,
              longitude: loc.lng,
              place_name: loc.name,
            },
          };
        } else if (field === "note") {
          updated[index] = { ...updated[index], note: value as string };
        }
        return updated;
      });
    }, []);

    const handleLocationTypeChange = useCallback((index: number, value: string) => {
      setPlaces((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          is_virtual: value === "online",
          location: value === "online" ? { action_url: "" } : { address: "", latitude: undefined, longitude: undefined },
        };
        return updated;
      });
    }, []);

    const addPlace = useCallback(() => {
      setPlaces((prev) => [...prev, { is_virtual: true, location: { action_url: "" }, note: "" }]);
    }, []);

    const removePlace = useCallback((index: number) => {
      setPlaces((prev) => prev.filter((_, i) => i !== index));
    }, []);

    // Task drafts are now owned by `ContextCommentDraftList`; the
    // legacy `addTaskDraft`/`removeTaskDraft`/`onTaskTitleChange`/
    // `onTaskDueChange` helpers are gone along with the inline
    // title-only task builder that lived in `ActionFields`.

    const submit = useCallback(async () => {
      if (!requireAuth()) return;

      const validationError = validateOrError();
      if (validationError) {
        setLifecycleError(validationError);
        setSuccess("");
        return;
      }

      // Task collection name is required (and owned by the user) when
      // the toggle is on. Guarding before the action is created avoids
      // orphaned actions whose collection step silently no-ops.
      const trimmedCollectionName = taskCollectionName.trim();
      if (createTaskCollection && trimmedCollectionName.length === 0) {
        setLifecycleError(taskUi.taskCollectionNameRequired);
        setSuccess("");
        return;
      }

      beginSubmit();
      setSuccess("");
      setTitleValidationMessage("");

      try {
        const properties: Record<string, unknown> = {
          start_at: startAt?.toISOString() || null,
          end_at: endAt?.toISOString() || null,
          places,
          sponsors: [],
          entity_type: "issue",
          entity_id: actionEntity?.id,
          privacy,
          is_private: isPrivate,
        };
        if (actionType) {
          properties.type = actionType;
        }

        const finalTitle = showTextField ? title.trim() || "[no_title]" : "[no_title]";

        // Inline task drafts get attached atomically by the backend
        // during `POST /api/posts`. Skipping bodies that are empty or
        // just `<p></p>` keeps the wire payload clean.
        const taskPrivacy = privacy;
        const inlineTaskDrafts: Record<string, unknown>[] = taskDrafts
          .map((t): Record<string, unknown> | null => {
            if (t.source === "existing") {
              return {
                local_id: t.localId,
                existing_comment_id: t.existingCommentId,
              };
            }
            const body = t.content.trim();
            if (!body || body === "<p></p>") {
              return null;
            }
            return {
              local_id: t.localId,
              content: body,
              privacy: taskPrivacy,
              timeline_ts: t.dueAt ? t.dueAt.toISOString() : null,
              properties: {
                required_participants: "all",
              },
            };
          })
          .filter((draft): draft is Record<string, unknown> => draft !== null);

        const actionData = {
          title: finalTitle,
          body: description.trim(),
          properties: { ...properties, privacy, is_private: isPrivate },
          properties_public: { is_private: isPrivate },
          entityType: "issue",
          entityId: actionEntity?.id,
          issueId: actionEntity?.id,
          privacy,
          targets: targets.map((t) => ({ entity_id: t.id, entity_type: "issue" })),
          tasks: inlineTaskDrafts.length > 0 ? inlineTaskDrafts : undefined,
        };

        const response = await createAction(actionData);
        const createdId = response.id;
        const props = response.properties;
        const privacyFromResponse =
          props && typeof props === "object" && props !== null
            ? readOptionalStringField(props, "privacy") ?? ""
            : "";
        const anonToken = readOptionalStringField(response, "anon_edit_token");

        if (privacyFromResponse === "anonymous" && anonToken) {
          await saveAnonToken("action", createdId, anonToken);
          addAnonRef({ type: "action", id: createdId });
        }

        if (coauthors.length > 0 && privacy !== "anonymous") {
          try {
            await addCoauthors("action", createdId, coauthorPairs);
          } catch {
            /* best-effort */
          }
        }

        // The backend returns the attached task comment ids alongside
        // the post in `context_comments.items`. We use that list for
        // the optional task-collection step below so the collection
        // points at the freshly-attached comments without a separate
        // round-trip.
        const responseRecord = response as unknown as Record<string, unknown>;
        const contextComments = responseRecord.context_comments;
        const createdTaskCommentIds: number[] = Array.isArray(
          (contextComments as { items?: unknown })?.items
        )
          ? ((contextComments as { items: Array<{ commentId?: unknown }> }).items
              .map((entry) =>
                typeof entry.commentId === "number" ? entry.commentId : null
              )
              .filter((value): value is number => typeof value === "number" && value > 0))
          : [];

        if (taskDrafts.length > 0) {
          if (createTaskCollection && createdTaskCommentIds.length > 0) {
            try {
              // The collection inherits the action's privacy. Anonymous
              // actions yield anonymous collections so the collection
              // post itself doesn't leak the author's identity.
              const collectionPrivacy = privacy;
              const createdCollection = await createCollection({
                issueId: actionEntity?.id ?? null,
                title: trimmedCollectionName,
                body: null,
                url: null,
                audience: collectionPrivacy,
                properties: {
                  privacy: collectionPrivacy,
                  collection_type: "standard",
                  source_action_id: createdId,
                },
              });
              for (let index = 0; index < createdTaskCommentIds.length; index += 1) {
                const taskCommentId = createdTaskCommentIds[index];
                await collectionApi.addItem(createdCollection.id, {
                  entityId: taskCommentId,
                  entityType: "comment",
                  properties: { sort_order: index },
                });
              }
            } catch {
              toast.error(taskUi.taskCollectionCreateError);
            }
          }
        }

        if (mm.media.length > 0) {
          try {
            await uploadMediaFiles(createdId);
          } catch {
            /* best-effort */
          }
        }

        try {
          if (!isPrivate) {
            toast.success("Action created", {
              duration: 3000,
              action: {
                label: "Highlight on profile",
                onClick: () => {
                  setNewActionId(createdId);
                  setShowProfilePrompt(true);
                },
              },
            });
          }
        } catch {
          /* ignore toast errors */
        }

        setSuccess("Action added successfully!");
        clearDraft();
        setTaskDrafts([]);
        setCreateTaskCollection(false);
        setTaskCollectionName("");
        onSuccess(response);
        navigate(`/action/${createdId}`);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr.response?.status === 401) {
          setShowAuthModal(true);
        } else {
          setLifecycleError("Failed to add action. Please try again.");
        }
      } finally {
        endSubmit();
      }
    }, [
      requireAuth,
      validateOrError,
      setLifecycleError,
      beginSubmit,
      endSubmit,
      showTextField,
      title,
      startAt,
      endAt,
      places,
      actionEntity,
      privacy,
      description,
      isPrivate,
      actionType,
      targets,
      onSuccess,
      navigate,
      setShowAuthModal,
      coauthors.length,
      coauthorPairs,
      clearDraft,
      uploadMediaFiles,
      mm,
      taskDrafts,
      createTaskCollection,
      taskCollectionName,
      taskUi.taskCollectionCreateError,
      taskUi.taskCollectionNameRequired,
    ]);

    const saveDraft = useCallback(async () => {
      if (!requireAuth()) return;
      const properties: Record<string, unknown> = {
        start_at: startAt?.toISOString() || null,
        end_at: endAt?.toISOString() || null,
        places,
        sponsors: [],
        entity_type: "issue",
        entity_id: actionEntity?.id,
        privacy,
        is_private: isPrivate,
        type: actionType || null,
        targets,
        draft_tasks: taskDrafts,
        create_task_collection: createTaskCollection,
        task_collection_name: taskCollectionName,
      };
      await createAction({
        status: "draft",
        title: showTextField ? title.trim() || "[no_title]" : "[no_title]",
        body: description.trim(),
        properties,
        entityType: "issue",
        entityId: actionEntity?.id,
        issueId: actionEntity?.id,
        privacy,
      });
      clearDraft();
      toast.success(getDraftUiStrings().draftSaved);
    }, [
      actionEntity?.id,
      actionType,
      clearDraft,
      createTaskCollection,
      description,
      endAt,
      isPrivate,
      places,
      privacy,
      requireAuth,
      showTextField,
      startAt,
      targets,
      taskCollectionName,
      taskDrafts,
      title,
    ]);

    useImperativeHandle(ref, () => ({ submit: () => void submit(), saveDraft, discardDraft: clearDraft }), [clearDraft, saveDraft, submit]);

    const titleField = useMemo(
      () => (
        <TitleField
          id="action-title"
          name="action-title"
          labelText="Title"
          fieldRequired={false}
          value={title}
          onChange={setTitle}
          maxLength={300}
          loading={false}
          showSkeletonWhenLoading={false}
          error={titleValidationMessage}
          validationState={
            titleValidationMessage ? "invalid" : !titleValidationMessage && title ? "valid" : "none"
          }
        />
      ),
      [title, titleValidationMessage]
    );

    return (
      <PostFormShell intro="Share an action being taken on the issue; actions can be used to organize change through specific tactics (e.g., protests, blackouts, rallies) online and offline; you can also specify targets the action is directed toward.">
        <DisplayOnProfilePrompt
          open={Boolean(showProfilePrompt && newActionId)}
          onClose={() => setShowProfilePrompt(false)}
          entityType="action"
          entityId={newActionId || 0}
          defaultPrivacy={
            (currentUser?.properties?.privacy_content_default_audience?.value as PrivacyLevel) || "public"
          }
        />
        <TargetsModal
          isOpen={targetsOpen}
          initialTargets={targets.map((t) => ({ id: t.id }))}
          onClose={() => setTargetsOpen(false)}
          onSave={(list) => setTargets(list.map((i) => ({ id: i.id })))}
        />

        <MediaSection
          idPrefix="action"
          mm={mm}
          showLink={false}
          showTitle={showTextField}
          onToggleLink={() => {}}
          onToggleTitle={() => setShowTextField((prev) => !prev)}
          includeLinkToggle={false}
          titleField={titleField}
        />

        <ActionFields
          actionType={actionType}
          onActionTypeChange={(v) => setActionType(v)}
          targetsCount={targets.length}
          onOpenTargets={() => setTargetsOpen(true)}
          startAt={startAt}
          endAt={endAt}
          onStartAtChange={setStartAt}
          onEndAtChange={setEndAt}
          userIsMobile={userIsMobile}
          places={places}
          onPlaceFieldChange={handlePlaceChange}
          onLocationTypeChange={handleLocationTypeChange}
          onAddPlace={addPlace}
          onRemovePlace={removePlace}
        />

        {/*
          "Tasks for this action" container. Owns the create-collection
          toggle, the required collection-name input (shown only when
          the toggle is on), and the task picker + drafts list rendered
          chrome-less so they visually belong to this container rather
          than a nested card.
        */}
        <div className="form-control rounded-lg border border-eoe-border-main p-3 space-y-3 dark:border-eoe-dark-border-main">
          <div className="flex items-center gap-2">
            <ListTodo className="size-5 shrink-0" aria-hidden />
            <span className="text-sm font-semibold">{taskUi.taskBuilderTitle}</span>
          </div>

          <div className="group flex items-center justify-between gap-3 rounded-md bg-eoe-bg-hover px-3 py-2 dark:bg-eoe-dark-bg-hover">
            <div className="min-w-0">
              <div className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
                {taskUi.createTaskCollectionLabel}
              </div>
              <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {taskUi.createTaskCollectionDescription}
              </p>
            </div>
            <input
              type="checkbox"
              className="toggle-switch-input flex-shrink-0"
              checked={createTaskCollection}
              onChange={(event) => setCreateTaskCollection(event.target.checked)}
              aria-label={taskUi.createTaskCollectionLabel}
            />
          </div>

          {createTaskCollection ? (
            <InputField
              id="action-task-collection-name"
              name="action-task-collection-name"
              type="text"
              labelText={taskUi.taskCollectionNameLabel}
              fieldPlaceholder={taskUi.taskCollectionNamePlaceholder}
              fieldRequired
              value={taskCollectionName}
              onChange={(event) => setTaskCollectionName(event.target.value)}
              className="w-full"
              maxLength={300}
            />
          ) : null}

          <ContextCommentDraftList
            kind="task"
            drafts={taskDrafts}
            onDraftsChange={setTaskDrafts}
            parentPrivacy={privacy}
            withDueDate
            isMobile={userIsMobile}
            renderChrome={false}
          />
        </div>

        <div className="form-control">
          <TextEditor value={description} onChange={setDescription} editorType="entity" placeholder="Body" />
        </div>

        {privacy !== "anonymous" ? (
          <CoauthorSection coauthors={coauthors} onAdd={addCoauthor} onRemove={removeCoauthor} />
        ) : null}

        <PrivacySection privacy={privacy} onPrivacyChange={setPrivacy} miniIcon={false} showDropdown />

        <FormStatusMessage error={lifecycle.error} success={success} auxiliary={mm.error} />

        {!hideActions ? (
          <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
            <button type="button" onClick={onCancel} className="btn-secondary w-full sm:w-fit py-2 px-4">
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                void submit();
              }}
              disabled={!lifecycle.canSubmit}
              className={`w-full sm:w-fit py-2 px-4 ${
                !lifecycle.canSubmit ? "btn-primary-disabled" : "btn-primary"
              } ${lifecycle.isSubmitting || mm.isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {lifecycle.isSubmitting || mm.isUploading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                </span>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        ) : null}
      </PostFormShell>
    );
  }
);

ActionPostForm.displayName = "ActionPostForm";

export default ActionPostForm;
